import React, { useState, useEffect, useRef } from "react";
import ReactSlider from "react-slider";

function Product2YearStrip({ onSliderChange, years, selectedYear, onYearUpdateComplete }) {
    // Sort years in ascending order (e.g., [2001, 2002, ..., 2024])
    const sortedYears = [...years].sort((a, b) => a.value - b.value);

    // Select years for labels: first, last, and every 4th year
    const labelInterval = 4;
    const labelYears = sortedYears.filter((year, index) => {
        return (
            index === 0 || // First year
            index === sortedYears.length - 1 || // Last year
            (index % labelInterval === 0 && // Every 4th year
                index !== sortedYears.length - 1) // Avoid duplicating last year
        );
    });

    const [sliderValue, setSliderValue] = useState(selectedYear.value || sortedYears[0]?.value || 2001);
    const [isPlaying, setIsPlaying] = useState(false);
    const sliderIntervalRef = useRef(null);
    const isUpdatingRef = useRef(false); // Prevent multiple simultaneous updates

    const handlePlayPause = () => {
        if (sliderValue === sortedYears[sortedYears.length - 1]?.value) {
            console.log(`Resetting to earliest year: ${sortedYears[0]?.value || 2001}`); // Debug log
            setSliderValue(sortedYears[0]?.value || 2001); // Reset to earliest year
        }
        setIsPlaying((prevState) => !prevState);
    };

    // Function to advance to the next year
    const advanceToNextYear = () => {
        if (isUpdatingRef.current) {
            console.log("Skipping advance: update in progress"); // Debug log
            return;
        }
        isUpdatingRef.current = true;
        setSliderValue((prevValue) => {
            const currentIndex = sortedYears.findIndex((y) => y.value === prevValue);
            const nextIndex = currentIndex + 1; // Move to next year (ascending)
            if (nextIndex >= sortedYears.length) {
                console.log("Reached last year, stopping and resetting"); // Debug log
                clearInterval(sliderIntervalRef.current);
                setIsPlaying(false);
                isUpdatingRef.current = false;
                return sortedYears[0]?.value || 2001; // Reset to earliest year
            }
            const nextYear = sortedYears[nextIndex].value;
            console.log(`Advancing to year: ${nextYear}`); // Debug log
            onSliderChange({ value: nextYear, label: nextYear });
            if (onYearUpdateComplete) {
                onYearUpdateComplete()
                    .then(() => {
                        console.log(`Completed update for ${nextYear}`); // Debug log
                        setTimeout(() => {
                            console.log(`Proceeding after 2s delay for ${nextYear}`); // Debug log
                            isUpdatingRef.current = false;
                        }, 2000); // 2-second delay
                    })
                    .catch((error) => {
                        console.error(`Error in onYearUpdateComplete for ${nextYear}:`, error);
                        isUpdatingRef.current = false; // Reset even on error
                    });
            } else {
                isUpdatingRef.current = false;
            }
            return nextYear;
        });
    };

    useEffect(() => {
        if (isPlaying) {
            console.log(`Play mode started, isPlaying: ${isPlaying}, sliderValue: ${sliderValue}`); // Debug log
            // Trigger first update immediately
            advanceToNextYear();
            // Set interval for subsequent updates
            sliderIntervalRef.current = setInterval(() => {
                advanceToNextYear();
            }, 3000); // 3-second interval
        } else {
            console.log("Play mode stopped, clearing interval"); // Debug log
            clearInterval(sliderIntervalRef.current);
        }

        return () => clearInterval(sliderIntervalRef.current);
    }, [isPlaying, sortedYears, onSliderChange, onYearUpdateComplete]);

    useEffect(() => {
        if (selectedYear.value && selectedYear.value !== sliderValue) {
            console.log(`Syncing sliderValue to selectedYear: ${selectedYear.value}`); // Debug log
            setSliderValue(selectedYear.value);
        }
    }, [selectedYear, sliderValue]);

    const handleSliderChange = (value) => {
        if (isUpdatingRef.current) {
            console.log("Skipping manual change: update in progress"); // Debug log
            return;
        }
        console.log(`Manual slider change to: ${value}`); // Debug log
        setSliderValue(value);
        onSliderChange({ value, label: value });
    };

    return (
        <div className="timeline-slider-container" style={{ left: "430px", width: "calc(98% - 670px)", padding: "30px 35px 2px 25px" }}>
            {/* <div className="playpause">
                <input
                    type="checkbox"
                    checked={isPlaying}
                    onChange={handlePlayPause}
                    id="playpause"
                    name="check"
                />
                <label htmlFor="playpause"></label>
            </div> */}
            <ReactSlider
                className="timeline-slider"
                thumbClassName="timeline-thumb"
                trackClassName="timeline-track"
                min={sortedYears[0]?.value || 2001} // Earliest year (e.g., 2001)
                max={sortedYears[sortedYears.length - 1]?.value || 2024} // Latest year (e.g., 2024)
                step={1}
                value={sliderValue}
                onChange={handleSliderChange}
                renderMark={(props) => {
                    // Only render marks for selected label years
                    if (labelYears.some((year) => year.value === props.value)) {
                        return (
                            <span {...props} className="timeline-mark">
                                {props.value}
                            </span>
                        );
                    }
                    return null;
                }}
            />
            <div className="timeline-labels">
                {labelYears.map((year) => (
                    <span key={year.value} className="timeline-label">
                        {year.value}
                    </span>
                ))}
            </div>
        </div>
    );
}

export default Product2YearStrip;